/* eslint-disable @next/next/no-img-element */
import Image from "next/image";
import React from "react";
import Button from "../../../components/Micro/Button/Button";
import SearchField from "../../../components/Micro/SearchField/SearchField";
import { AiOutlineCheck } from "react-icons/ai";
import { BsDot, BsCart2 } from "react-icons/bs";
import { MdErrorOutline } from "react-icons/md";
import Collapse from "../../../components/Micro/Collapse/Collapse";
import Link from "next/link";
import Skeleton from "../../../components/Micro/Skeleton/Skeleton";
import { RiDeleteBinLine } from "react-icons/ri";
import CheckBoxComponent from "../../../components/Micro/CheckBoxComponent/CheckBoxComponent";
import { useRouter } from "next/router";
import { MdKeyboardArrowLeft } from "react-icons/md";
import Sidebar from "../../../components/Micro/Sidebar/Sidebar";
import { ModalContent } from "../../../components/Micro/Modal/Modal";
import {
  handleRedirectToPayment,
  searchDomains,
} from "../../../backend/domains";
import { axios_server, handleRequestError } from "../../../backend";
import Seo from "../../../components/Micro/Seo";
import useLoading from "../../../hooks/useLoading";
import ToggleButton from "../../../components/Micro/ToggleButton";
import config from "../../../config";
import { DomainPaymentStorageType, DomainPurchaseType } from "../../../types";
import { NextPage } from "next";
import useUser from "@/hooks/useUser";
import { cn } from "@/utils";
import useSWR from "swr";

const New: NextPage = () => {
  const router = useRouter();
  const { user } = useUser();
  const { loading, start, stop } = useLoading();
  const privacyProtectionPrice = 8;
  const searchQuery = (router.query.q as string) || "";
  const [cart, setCart] = React.useState<DomainPurchaseType[]>([]);
  const [inputValue, setInputValue] = React.useState<string>(searchQuery || "");
  const [value, setValue] = React.useState<string>(searchQuery || "");
  const [isCart, setIsCart] = React.useState<boolean>(false);
  const [availableDomains, setAvailableDomains] = React.useState<
    DomainPurchaseType[]
  >([]);

  const { data } = useSWR<{ data: { data: DomainPurchaseType[] } }>(
    "/v1/domains/suggestions",
    axios_server.get,
  );

  const [paymentStorage, setPaymentStorage] =
    React.useState<DomainPaymentStorageType>({
      total: 0,
      privacyProtection: false,
      selectedYears: 1,
      autoRenewal: false,
    });

  React.useEffect(() => {
    if (searchQuery) searchAvailableDomain({ cached: true });
  }, []);

  const projectId = (router.query.project as string) || "";

  const searchAvailableDomain = async ({
    cached,
    query,
  }: { cached?: boolean; query?: string } = {}) => {
    if (!cached) setAvailableDomains([]);

    const value = query
      ? query
      : inputValue.includes(".")
      ? inputValue
      : `${inputValue}.com`;

    setValue(value);
    setInputValue("");

    start();
    searchDomains(value)
      .then((res) => {
        const data: DomainPurchaseType[] = res?.data?.data;
        setAvailableDomains(data);
      })
      .catch(handleRequestError)
      .finally(() => stop());
  };

  const isAvailable: DomainPurchaseType | undefined = availableDomains.find(
    (domain) => value === domain.domainName,
  );

  const handleReset = () => {
    if (value === "") {
      router.replace("/domains");
    } else {
      // router.replace("/domains/new");
      setValue("");
      setAvailableDomains([]);
    }
  };

  const total = React.useMemo(() => {
    const sum = paymentStorage.privacyProtection
      ? privacyProtectionPrice +
        cart.reduce((total, item) => total + item.purchasePrice, 0) *
          paymentStorage.selectedYears
      : cart.reduce((total, item) => total + item.purchasePrice, 0) *
        paymentStorage.selectedYears;

    return Number(sum.toFixed(2));
  }, [cart, paymentStorage.privacyProtection, paymentStorage.selectedYears]);

  React.useEffect(() => {
    const cartValue = localStorage.getItem("cart");
    if (cartValue) {
      setCart([JSON.parse(cartValue)]);
    }
  }, []);

  const handleAddToCart = (
    domain: DomainPurchaseType,
    isSuggested?: boolean,
  ) => {
    setCart([domain]);
    localStorage.setItem("cart", JSON.stringify(domain));
    if (isSuggested) {
      setValue(domain.domainName);
      setAvailableDomains(data?.data.data || []);
    }
  };

  const removeItemFromCart = () => {
    setCart([]);
    setPaymentStorage({
      privacyProtection: false,
      selectedYears: 1,
      autoRenewal: false,
    });
    localStorage.removeItem("cart");
    localStorage.removeItem("paymentDetails");
  };
  return (
    <>
      <Seo
        title="Purchase a domain"
        description="Bring what you do online with Brimble; get a domain a now"
      />
      <main className="w-full absolute left-0 right-0 min-h-[100vh] bg-[#F7F9FD] dark:bg-theme-color ">
        <div className="bg-[url('https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776922/dashboard-assets/register_hrlw63.svg')] lg:h-[620px] h-[461px]">
          <div className="container h-full mx-auto">
            <nav className="flex items-center justify-between w-full pt-6 lg:justify-start lg:px-10">
              <div className="flex items-center lg:space-x-3">
                <div
                  className="cursor-pointer lg:w-10 lg:h-10 h-12 w-12 flex items-center justify-center bg-[#232D41]"
                  onClick={handleReset}
                >
                  <MdKeyboardArrowLeft className="text-xl text-primary" />
                </div>
                <p className="hidden text-white lg:block whitespace-nowrap">
                  Back to domains
                </p>
              </div>
              <div className="items-center justify-center hidden w-full text-center lg:block ">
                <Link href="/domains">
                  <Image
                    src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776907/dashboard-assets/nav_k7dmkl.svg"
                    width={214}
                    height={24}
                    alt="image"
                    className="cursor-pointer"
                  />
                </Link>
              </div>
              <div
                className="w-12 h-12 rounded-full lg:hidden bg-[#232D41] border border-[#ACACAC]"
                onClick={() => {
                  setIsCart(true);
                }}
              >
                <div className="flex items-center justify-center h-full">
                  <BsCart2 className="text-2xl text-white" />
                </div>
                <p className="relative flex items-center justify-center w-5 h-5 text-white rounded-full bg-danger bottom-12 left-9">
                  {cart.length}
                </p>
                <CartSideBar
                  isCart={isCart}
                  handleCartBar={() => setIsCart(false)}
                  cart={cart}
                  setCart={setCart}
                  total={total}
                  paymentStorage={paymentStorage}
                  setPaymentStorage={setPaymentStorage}
                  privacyProtectionPrice={privacyProtectionPrice}
                  removeItemFromCart={removeItemFromCart}
                />
              </div>
            </nav>
            <header className="flex flex-col justify-center pt-20 text-white lg:items-center lg:py-0 lg:h-full">
              <div className="items-center justify-center block w-full mb-6 text-center lg:hidden">
                <Link href="/domains">
                  <Image
                    src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776907/dashboard-assets/nav_k7dmkl.svg"
                    width={173}
                    height={22}
                    alt="image"
                    className="cursor-pointer"
                  />
                </Link>
              </div>
              <p className="max-w-3xl text-3xl font-extrabold text-center lg:text-7xl md:text-4xl">
                Create an <span className="text-primary">Online</span> Presence
                for yourself
              </p>
            </header>
          </div>
        </div>

        <div className="container mx-auto">
          <div className="relative flex flex-col items-center justify-center px-3 py-1 bg-white shadow-sm lg:flex-row lg:space-x-5 lg:h-fit h-44 lg:bottom-10 bottom-32 dark:bg-theme-color-light">
            <SearchField
              type="search"
              containerClass="lg:border-b-0 lg:border-0 border-opacity-40 lg:!mb-0"
              placeholder="Find the perfect domain"
              className="placeholder:text-gray-dark lg:placeholder:text-base placeholder:text-[17px] placeholder:font-medium mb-0"
              value={inputValue}
              onChange={(e) => setInputValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") searchAvailableDomain();
              }}
            />
            <Button
              className={`rounded-md ${
                !!loading || !inputValue.length ? "cursor-not-allowed" : ""
              }`}
              onClick={() => searchAvailableDomain()}
              loading={!!loading}
              disabled={!!loading || !inputValue.length}
            >
              Search Domain
            </Button>
            <div
              className={cn(
                "absolute left-0 w-full h-0 z-50  bg-white dark:bg-theme-color-light top-[90%] rounded-b-xl !ml-0 transition-all duration-300 ease-out shadow",
                {
                  "h-96  pb-10": inputValue.length >= 3,
                  "h-0 overflow-hidden ": inputValue.length === 0,
                },
              )}
            >
              <ul
                className={cn(
                  "h-full px-5  overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800",
                  {
                    hidden: inputValue.length < 3,
                  },
                )}
              >
                {inputValue.length >= 3 &&
                  extensions.map((ext) => {
                    const extractName = inputValue.startsWith("www.")
                      ? inputValue.split(".")[1]
                      : inputValue.split(".")[0];

                    return (
                      <li
                        key={ext}
                        className="flex items-center py-3 cursor-pointer dark:text-gray-light"
                        onClick={() =>
                          searchAvailableDomain({
                            query: `${extractName}.${ext}`,
                          })
                        }
                      >
                        <p className="text-xl font-medium">
                          www.{extractName}.
                          <span className="text-gray-300 dark:text-gray-dark">
                            {ext}
                          </span>
                        </p>
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>

          {loading && (
            <div className="relative p-3 mb-5 bg-white dark:bg-theme-color-light rounded-xl lg:p-5 lg:static bottom-24">
              <div className="block py-3 border-b border-opacity-30 dark:border-gray-dark lg:hidden">
                {value}
              </div>
              <div className="flex flex-col py-2 lg:items-center lg:flex-row lg:space-x-3 lg:py-0">
                <div className="py-2 lg:py-0">
                  <h1 className="text-lg font-semibold lg:text-2xl">
                    Searching for domain availability...
                  </h1>
                  <p className="py-1 text-sm text-gray-dark lg:text-base">
                    This may take only a few seconds
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && value ? (
            <div className="relative p-3 mb-5 bg-white dark:bg-theme-color-light lg:p-5 lg:static bottom-24">
              <div className="block py-3 border-b border-opacity-30 dark:border-gray-dark lg:hidden">
                {value}
              </div>
              <div className="flex flex-col justify-between lg:flex-row">
                <div className="flex flex-col py-2 lg:items-center lg:flex-row lg:space-x-3 lg:py-0">
                  <div
                    className={`flex items-center justify-center rounded-full lg:h-16 lg:w-16 h-11 w-11  ${
                      isAvailable ? "bg-[#D0FBE6]" : "bg-pink-100"
                    }`}
                  >
                    {isAvailable ? (
                      <AiOutlineCheck className="text-3xl text-success lg:text-4xl" />
                    ) : (
                      <MdErrorOutline className="text-3xl text-error lg:text-4xl" />
                    )}
                  </div>
                  <div className="py-2 lg:py-0">
                    <h1
                      className={`text-lg font-semibold lg:text-2xl ${
                        isAvailable ? "text-success" : "text-danger"
                      }`}
                    >
                      {isAvailable
                        ? `${value} is available`
                        : `${value} is unavailable`}
                    </h1>
                    {isAvailable && (
                      <p className="py-1 text-lg lg:text-2xl">
                        ${isAvailable.purchasePrice}
                      </p>
                    )}
                  </div>
                </div>

                {isAvailable && cart[0]?.domainName !== value && (
                  <Button
                    className="h-8  border-0 font-extrabold text-sm hover:shadow-sm dark:bg-primary bg-[#F3F6FC] dark:text-white text-primary hover:bg-light"
                    onClick={() =>
                      handleAddToCart({ ...isAvailable, projectId })
                    }
                  >
                    Add to Cart
                  </Button>
                )}
              </div>
            </div>
          ) : null}

          <section className="relative flex flex-col lg:flex-row lg:static bottom-24 gap-x-5">
            {(loading || value) && (
              <div className="lg:w-[64%] bg-white dark:bg-theme-color-light p-5 mb-5">
                {!loading && (
                  <>
                    <h3 className="text-base font-semibold lg:text-2xl">
                      {availableDomains.length > 0
                        ? "You could try out a few others listed below"
                        : "Here are some alternative domains you might like"}
                    </h3>
                    <p className="py-1 text-xs text-gray-dark dark:text-gray-light md:text-base">
                      {availableDomains.length > 0
                        ? "Here are a few domain name alternatives you can purchase"
                        : "We've found some other available domains that might work for you"}
                    </p>
                  </>
                )}

                {loading ? (
                  <div className="w-full space-y-3">
                    {Array.from({ length: 8 }).map((_, i: number) => (
                      <AvailableDomainsSkeleton key={i} />
                    ))}
                  </div>
                ) : availableDomains?.length > 0 ? (
                  availableDomains?.map((domain) => (
                    <div
                      className="flex flex-col justify-between px-3 py-2 my-5 border lg:flex-row lg:items-center dark:text-gray-light dark:bg-theme-color dark:border-0"
                      key={domain.domainName}
                    >
                      <div className="flex items-center py-2 space-x-2 border-b lg:py-0 lg:border-b-0 border-opacity-30 dark:border-gray-dark">
                        <p className="text-base lg:text-lg">
                          {domain.domainName?.toLowerCase()}
                        </p>
                        {domain.isDiscounted && (
                          <span className="p-2 text-xs bg-yellow-100 rounded-md shadow-sm dark:text-yellow-800">
                            ${config.DOMAIN_DISCOUNT_PERCENTAGE}% off 🚀
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col py-2 lg:flex-row lg:items-center lg:space-x-10">
                        <p className="py-2 text-base lg:text-black dark:text-gray-light text-gray-dark">
                          ${domain.purchasePrice}
                        </p>

                        <Button
                          className="h-8 text-sm font-extrabold border-0 w-fit bg-light hover:shadow-sm hover:bg-light"
                          onClick={() => handleAddToCart(domain)}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                  ))
                ) : data?.data?.data ? (
                  data.data.data.map((domain) => (
                    <div
                      className="flex flex-col justify-between px-3 py-2 my-5 border lg:flex-row lg:items-center dark:text-gray-light dark:bg-theme-color dark:border-0 rounded-xl"
                      key={domain.domainName}
                    >
                      <div className="flex items-center py-2 space-x-2 border-b lg:py-0 lg:border-b-0 border-opacity-30 dark:border-gray-dark">
                        <p className="text-base lg:text-lg">
                          {domain.domainName}
                        </p>
                        {domain.isDiscounted && (
                          <span className="p-2 text-xs bg-yellow-100 rounded-md shadow-sm dark:text-yellow-800">
                            ${config.DOMAIN_DISCOUNT_PERCENTAGE}% off 🚀
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col py-2 lg:flex-row lg:items-center lg:space-x-10">
                        <p className="py-2 text-base lg:text-black dark:text-gray-light text-gray-dark">
                          ${domain.purchasePrice}
                        </p>

                        <Button
                          className="h-8 text-sm font-extrabold border-0 w-fit bg-light hover:shadow-sm hover:bg-light"
                          onClick={() => handleAddToCart(domain)}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            )}

            {(loading || value) && (
              <div className="lg:w-[37%] bg-white dark:bg-theme-color-light p-5 h-fit my-5 lg:my-0 lg:block hidden">
                <div className="mb-3">
                  <h3 className="text-2xl font-semibold">Cart</h3>
                </div>
                <div>
                  {cart?.length === 0 ? (
                    <>
                      <div className="block w-full pt-2 pb-5 dark:hidden">
                        <img
                          src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776793/dashboard-assets/cart_gvymqt.svg"
                          alt="cart"
                          className="w-full"
                        />
                      </div>
                      <div className="hidden w-full pt-2 pb-5 dark:block">
                        <img
                          src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776931/dashboard-assets/shopping-cart_y67guo.svg"
                          alt="cart"
                          className="w-full"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="flex border dark:border-0 w-12/12">
                      <Collapse
                        className="w-full p-0 bg-white dark:bg-theme-color rounded-none"
                        title={
                          <div className="flex space-x-3">
                            <p className="truncate max-w-[100px] xl:max-w-[150px]">
                              {cart[0]?.domainName}
                            </p>
                            <div
                              className={`text-primary flex bg-light w-fit h-8 text-xs font-medium  justify-center items-center p-2`}
                            >
                              1 YR
                              <span>
                                <BsDot />
                              </span>
                              ${cart[0]?.purchasePrice}
                            </div>
                          </div>
                        }
                      >
                        {
                          <div className="flex flex-col space-y-5 text-lg mt-2">
                            <select
                              onChange={(e) =>
                                setPaymentStorage({
                                  ...paymentStorage,
                                  selectedYears: Number(e.target.value),
                                })
                              }
                              className="flex items-center w-full space-x-2 text-base font-normal bg-light dark:bg-theme-color-light select h-14 rounded-none"
                            >
                              <option
                                value={1}
                                className="h-14"
                                selected={!cart[0]?.domainName?.endsWith(".ai")}
                                disabled={cart[0]?.domainName?.endsWith(".ai")}
                              >
                                1 Year
                              </option>
                              <option
                                value={2}
                                selected={cart[0]?.domainName?.endsWith(".ai")}
                              >
                                {" "}
                                2 Years
                              </option>
                              <option value={3}> 3 Years</option>
                              <option value={4}> 4 Years</option>
                              <option value={5}> 5 Years</option>
                              <option value={6}> 6 Years</option>
                              <option value={7}> 7 Years</option>
                              <option value={8}> 8 Years</option>
                              <option value={9}> 9 Years</option>
                              <option value={10}> 10 Years</option>
                            </select>
                            <div className="flex items-center space-x-2 p-4 bg-[#F3F6FC] dark:bg-theme-color">
                              <ToggleButton
                                checked={paymentStorage?.autoRenewal}
                                onChange={(e) =>
                                  setPaymentStorage({
                                    ...paymentStorage,
                                    autoRenewal: e.target.checked,
                                  })
                                }
                                disabled={cart[0]?.renewalPrice == null}
                              />
                              <span>Auto Renewal</span>
                            </div>
                            {!cart[0]?.isFree ? (
                              <div className="flex items-center p-3 space-x-3 text-base border border-primary">
                                <CheckBoxComponent
                                  checked={
                                    paymentStorage.privacyProtection ||
                                    cart[0]?.domainName?.endsWith(".app")
                                  }
                                  disabled={cart[0]?.domainName?.endsWith(
                                    ".app",
                                  )}
                                  onChange={(e) => {
                                    setPaymentStorage({
                                      ...paymentStorage,
                                      privacyProtection:
                                        e.currentTarget.checked,
                                    });
                                  }}
                                />
                                {cart[0]?.domainName?.endsWith(".app") ? (
                                  <span className="font-medium">
                                    Privacy protection •{" "}
                                    <span className="italic text-gray-500">
                                      Free
                                    </span>
                                  </span>
                                ) : (
                                  <span className="font-medium">
                                    Privacy protection • $
                                    {privacyProtectionPrice}.00
                                  </span>
                                )}
                              </div>
                            ) : null}
                          </div>
                        }
                      </Collapse>
                      <div className="mx-2 my-4 h-fit">
                        <div
                          className="p-2 bg-pink-100 rounded-full cursor-pointer hover:shadow-sm text-danger"
                          onClick={() => removeItemFromCart()}
                        >
                          <RiDeleteBinLine className="" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  {cart?.length !== 0 && (
                    <div className="flex items-center justify-between w-full px-3 py-4 mt-5 text-lg border dark:border-opacity-20 dark:border-gray-dark rounded-xl">
                      <p>Total</p>
                      <p className="font-semibold">${total}</p>
                    </div>
                  )}
                </div>
                <div className="w-full py-3">
                  <Button
                    className="w-full"
                    onClick={() =>
                      handleRedirectToPayment({
                        total,
                        privacyProtection: paymentStorage.privacyProtection,
                        selectedYears: paymentStorage.selectedYears,
                        autoRenewal: paymentStorage.autoRenewal,
                        userId: user?.id,
                      })
                    }
                    disabled={cart.length === 0}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* Take action */}
          {!value && (
            <div className="w-full pb-10 space-y-5">
              <h2 className="text-xl font-semibold">Take Action</h2>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                {/* connect domain */}
                <div className="flex flex-col justify-between w-full p-5 bg-light aspect-square">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full dark:bg-theme-color">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.9993 29.3346C23.3631 29.3346 29.3327 23.3651 29.3327 16.0013C29.3327 8.63751 23.3631 2.66797 15.9993 2.66797C8.63555 2.66797 2.66602 8.63751 2.66602 16.0013C2.66602 23.3651 8.63555 29.3346 15.9993 29.3346Z"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2.66602 16H29.3327"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.9993 2.66797C19.3344 6.31911 21.2297 11.0573 21.3327 16.0013C21.2297 20.9453 19.3344 25.6835 15.9993 29.3346C12.6643 25.6835 10.769 20.9453 10.666 16.0013C10.769 11.0573 12.6643 6.31911 15.9993 2.66797V2.66797Z"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <div className="px-3 py-1.5 text-center bg-white dark:bg-theme-color rounded-full w-fit">
                      <p className="text-xs font-light uppercase text-primary">
                        take action
                      </p>
                    </div>
                    <h3 className="text-2xl font-medium">Connect domain</h3>
                    <p className="text-sm font-normal text-gray-500 dark:text-gray-300">
                      Already have a domain? Connect it in seconds and start
                      building your project.
                    </p>
                    <p>
                      <Link href={"https://documentation.brimble.app"}>
                        <a className="text-base italic font-bold underline text-primary">
                          Connect your Domain
                        </a>
                      </Link>
                    </p>
                  </div>
                </div>
                {/* resources */}
                <div className="flex flex-col justify-between w-full p-5 bg-light aspect-square">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full dark:bg-theme-color">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.33398 26.0013C5.33398 24.1604 6.82637 22.668 8.66732 22.668H26.6673"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.66732 2.66797H26.6673V29.3346H8.66732C6.82637 29.3346 5.33398 27.8423 5.33398 26.0013V6.0013C5.33398 4.16035 6.82637 2.66797 8.66732 2.66797Z"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <div className="px-3 py-1.5 text-center bg-white dark:bg-theme-color rounded-full w-fit">
                      <p className="text-xs font-light uppercase text-primary">
                        resources
                      </p>
                    </div>
                    <h3 className="text-2xl font-medium">
                      Learn About Domains
                    </h3>
                    <p className="text-sm font-normal text-gray-500 dark:text-gray-300">
                      Learn how the right domain can elevate your brand and grow
                      your online presence.
                    </p>
                    <p>
                      <Link href={"https://documentation.brimble.app"}>
                        <a className="text-base italic font-bold underline text-primary">
                          Check out our Docs
                        </a>
                      </Link>
                    </p>
                  </div>
                </div>
                {/* DNS */}
                <div className="flex flex-col justify-between w-full p-5  bg-light aspect-square">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full dark:bg-theme-color">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M26.986 17.6527C30.1119 14.5268 30.1119 9.45866 26.986 6.33273C23.8601 3.2068 18.7919 3.2068 15.666 6.33273L6.66602 15.3327V26.6661H17.9993L26.986 17.6527Z"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21.3327 12L2.66602 30.6667"
                        stroke="#5580D5"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M22.6667 21.332H12"
                        stroke="#011F3D"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="space-y-2">
                    <div className="px-3 py-1.5 text-center bg-white dark:bg-theme-color rounded-full w-fit">
                      <p className="text-xs font-light uppercase text-primary">
                        blog post
                      </p>
                    </div>
                    <h3 className="text-2xl font-medium">
                      DNS. Sounds strange?
                    </h3>
                    <p className="text-sm font-normal text-gray-500 dark:text-gray-300">
                      Learn how the right domain can elevate your brand and grow
                      your online presence.
                    </p>
                    <p>
                      <Link href={"https://documentation.brimble.app"}>
                        <a className="text-base italic font-bold underline text-primary">
                          View our Blog
                        </a>
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-medium">
                Domains recommended for you
              </h2>
              <ul className="space-y-5">
                {data?.data?.data?.map((domain) => (
                  <div
                    className="hover:bg-gray-100 dark:hover:bg-theme-color-light/40 flex flex-col justify-between px-6 bg-white lg:flex-row lg:items-center dark:text-gray-light dark:bg-theme-color-light cursor-pointer"
                    key={domain.domainName}
                  >
                    <div className="flex items-center py-2 space-x-2 border-b lg:py-0 lg:border-b-0 border-opacity-30 dark:border-gray-dark">
                      <p className="text-base lg:text-lg">
                        {domain.domainName}
                      </p>
                      {domain.isDiscounted && (
                        <span className="p-2 text-xs bg-yellow-100 rounded-md shadow-sm dark:text-yellow-800">
                          ${config.DOMAIN_DISCOUNT_PERCENTAGE}% off 🚀
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col py-2 lg:flex-row lg:items-center lg:space-x-10">
                      <p className="py-2 text-base lg:text-black dark:text-gray-light text-gray-dark">
                        ${domain.purchasePrice}
                      </p>

                      <Button
                        className="h-8 px-5 text-sm font-extrabold border-0 w-fit bg-light hover:shadow-sm hover:bg-light dark:!bg-theme-color"
                        onClick={() => handleAddToCart(domain, true)}
                      >
                        Add to Cart
                      </Button>
                    </div>
                  </div>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default New;

const extensions = ["com", "net", "org", "io", "ai", "app", "dev", "co"];

const CartSideBar = ({
  isCart,
  handleCartBar,
  cart,
  setCart,
  total,
  paymentStorage,
  setPaymentStorage,
  privacyProtectionPrice,
  removeItemFromCart,
}: {
  isCart: boolean;
  handleCartBar: () => void;
  cart: DomainPurchaseType[];
  setCart: (by: string[] | any) => void;
  total: number;
  paymentStorage: DomainPaymentStorageType;
  setPaymentStorage: (by: DomainPaymentStorageType) => void;
  privacyProtectionPrice: number;
  removeItemFromCart: () => void;
}) => {
  const { user } = useUser();
  React.useEffect(() => {
    const cartValue = localStorage.getItem("cart");
    if (cartValue) {
      setCart([JSON.parse(cartValue)]);
    }
  }, []);

  return (
    <Sidebar
      open={isCart}
      onClose={handleCartBar}
      className="z-50 overflow-y-auto bg-white dark:bg-theme-color"
    >
      <ModalContent className="rounded-b-xl h-[100vh] w-full absolute dark:!bg-theme-color md:static">
        <main className="flex flex-col justify-between h-full p-5">
          <div>
            <nav>
              <div className="flex items-center space-x-3">
                <div
                  className="cursor-pointer lg:w-10 lg:h-10 h-12 w-12 rounded-lg flex items-center justify-center bg-[#EBF0FA] dark:bg-[#232D41]"
                  onClick={() => {
                    handleCartBar();
                  }}
                >
                  <MdKeyboardArrowLeft className="text-xl text-primary" />
                </div>
                <p className="text-primary">Back</p>
              </div>
            </nav>

            <div className="mt-10">
              <div>
                <h1 className="text-3xl font-bold">Cart</h1>
                <p className="text-gray-dark">
                  {cart.length} item in your cart
                </p>
              </div>
              <div className="flex flex-col justify-center h-20 p-5 my-5 bg-light rounded-xl">
                <h3 className="py-1 text-sm text-gray-dark">Total Amount</h3>
                <p className="text-xl font-extrabold">${total}</p>
              </div>
              <div>
                {cart?.length === 0 ? (
                  <div className="p-5 bg-cover h-[471px] rounded-xl bg-light flex flex-col items-center justify-center">
                    <div className="block dark:hidden">
                      <Image
                        src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776793/dashboard-assets/cart_gvymqt.svg"
                        alt="cart"
                        width={180}
                        height={180}
                      />
                    </div>
                    <div className="hidden dark:block">
                      <Image
                        src="https://res.cloudinary.com/dgqfojhx4/image/upload/v1708776931/dashboard-assets/shopping-cart_y67guo.svg"
                        alt="cart"
                        width={180}
                        height={180}
                      />
                    </div>
                    <div className="text-center ">
                      <h3 className="text-xl font-semibold">
                        Your Cart is Empty
                      </h3>
                      <p className="text-sm text-gray-dark">
                        You do not have any domain in your cart at the moment
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex border dark:border-0 rounded-xl w-12/12">
                    <Collapse
                      className="p-0 bg-white dark:bg-theme-color-light w-[97%]"
                      title={
                        <div className="flex space-x-3 ">
                          <p className="truncate sm:max-w-[200px] md:max-w-[500px] lg:max-w-[none] sm:text-base text-sm">
                            {cart[0]?.domainName}
                          </p>
                          <div
                            className={`text-primary sm:flex hidden bg-[#F3F6FC] dark:bg-theme-color  rounded-full w-fit h-8 text-xs font-medium  justify-center items-center p-2`}
                          >
                            1 YR
                            <span>
                              <BsDot />
                            </span>
                            ${cart[0]?.purchasePrice}
                          </div>
                        </div>
                      }
                    >
                      {
                        <div className="flex flex-col space-y-5 text-lg">
                          <select
                            onChange={(e) =>
                              setPaymentStorage({
                                ...paymentStorage,
                                selectedYears: Number(e.target.value),
                              })
                            }
                            className="flex bg-[#F3F6FC] dark:bg-theme-color select h-14 items-center space-x-2 rounded-xl w-full text-base font-normal my-2"
                          >
                            <option value={1} className="h-14" selected>
                              1 Year
                            </option>
                            <option value={2}> 2 Years</option>
                            <option value={3}> 3 Years</option>
                            <option value={4}> 4 Years</option>
                            <option value={5}> 5 Years</option>
                            <option value={6}> 6 Years</option>
                            <option value={7}> 7 Years</option>
                            <option value={8}> 8 Years</option>
                            <option value={9}> 9 Years</option>
                            <option value={10}> 10 Years</option>
                          </select>
                          <div className="flex items-center space-x-2 p-4 bg-[#F3F6FC] dark:bg-theme-color rounded-xl">
                            <ToggleButton
                              checked={paymentStorage?.autoRenewal}
                              onChange={(e) =>
                                setPaymentStorage({
                                  ...paymentStorage,
                                  autoRenewal: e.target.checked,
                                })
                              }
                              disabled={cart[0]?.renewalPrice == null}
                            />
                            <span>Auto Renewal</span>
                          </div>
                          {!cart[0]?.isFree ? (
                            <div className="flex items-center p-3 space-x-2 text-base border border-primary rounded-xl">
                              <CheckBoxComponent
                                checked={paymentStorage.privacyProtection}
                                onChange={(e) => {
                                  setPaymentStorage({
                                    ...paymentStorage,
                                    privacyProtection: e.currentTarget.checked,
                                  });
                                }}
                              />
                              <span className="text-sm font-medium lg:text-base">
                                Privacy protection • ${privacyProtectionPrice}
                                .00
                              </span>
                            </div>
                          ) : null}
                        </div>
                      }
                    </Collapse>
                    <div className="mx-2 my-4 h-fit">
                      <div
                        className="p-2 bg-pink-100 rounded-full cursor-pointer hover:shadow-sm text-danger"
                        onClick={() => removeItemFromCart()}
                      >
                        <RiDeleteBinLine />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <div className="w-full py-10">
              <Button
                className="w-full"
                onClick={() =>
                  handleRedirectToPayment({
                    total,
                    privacyProtection: paymentStorage.privacyProtection,
                    selectedYears: paymentStorage.selectedYears,
                    autoRenewal: paymentStorage.autoRenewal,
                    userId: user?.id,
                  })
                }
                disabled={cart.length == 0}
              >
                Continue to Checkout
              </Button>
            </div>
          </div>
        </main>
      </ModalContent>
    </Sidebar>
  );
};

const AvailableDomainsSkeleton = () => {
  return (
    <div className="flex flex-col justify-between px-3 py-3 my-5 border lg:flex-row lg:items-center dark:border-gray-dark dark:border-opacity-20 dark:bg-theme-color rounded-xl">
      <div className="w-40 rounded-md bg-light h-9">
        <Skeleton />
      </div>
      <div className="flex flex-col py-3 lg:flex-row lg:space-x-10 lg:items-center">
        <div className="w-20 rounded-md h-9 bg-light dark:bg-theme-color-lighter">
          <Skeleton />
        </div>
        <div className="w-24 mt-2 h-9 bg-light lg:mt-0 rounded-xl">
          <Skeleton />
        </div>
      </div>
    </div>
  );
};
